/**
 * TrialCodesSection — iter71
 *
 * Compact CRUD widget for managing trial/promo codes. Rendered above
 * the Subscriptions table in Site Admin → Subscriptions tab.
 *
 * Today the codes are a *reference list* — the registration page accepts
 * any non-empty string in the trial_code field, and the admin double-
 * checks against this list at approval time. A follow-up will tighten
 * validation server-side.
 *
 * Capabilities:
 *   - List all codes (newest first)
 *   - Create (uppercase, alphanumeric/-/_, optional description + max_uses)
 *   - Toggle active/inactive inline
 *   - Edit description + max_uses inline
 *   - Delete with confirm
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Edit2, Check, X, Loader2, ChevronDown, ChevronUp } from "lucide-react";

export default function TrialCodesSection() {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.listTrialCodes();
            setCodes(r.codes || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load trial codes");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleActive = async (c) => {
        try {
            const r = await api.updateTrialCode(c.id, { active: !c.active });
            setCodes((prev) => prev.map((x) => (x.id === c.id ? r.code : x)));
            toast.success(`${c.code} ${r.code.active ? "activated" : "deactivated"}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to toggle code");
        }
    };

    const removeCode = async (c) => {
        if (!window.confirm(`Delete trial code "${c.code}"? This cannot be undone.`)) return;
        try {
            await api.deleteTrialCode(c.id);
            setCodes((prev) => prev.filter((x) => x.id !== c.id));
            toast.success(`Deleted ${c.code}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete code");
        }
    };

    const active = codes.filter((c) => c.active).length;

    return (
        <section className="brutal-card p-4 space-y-3" data-testid="trial-codes-section">
            <header className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-volt" />
                    <h3 className="font-display font-extrabold uppercase text-sm tracking-wide">Trial Codes</h3>
                    <span className="text-[11px] text-zinc-500">
                        {active} active / {codes.length} total
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setCreateOpen(true)}
                        data-testid="trial-codes-create-btn"
                        className="brutal-btn-primary text-xs py-1.5"
                    >
                        <Plus className="w-3 h-3" /> New code
                    </button>
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        data-testid="trial-codes-collapse-btn"
                        className="brutal-btn-ghost text-xs py-1.5"
                    >
                        {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                    </button>
                </div>
            </header>

            <p className="text-[11px] text-zinc-500 leading-relaxed">
                Approved codes for Trial-tier signups. Today the registration page accepts any non-empty
                code (admin verifies at approval). Future: codes here will be the source of truth.
            </p>

            {!collapsed && (
                <>
                    {loading ? (
                        <div className="flex items-center gap-2 text-zinc-400 py-4 justify-center" data-testid="trial-codes-loading">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                    ) : codes.length === 0 ? (
                        <div className="text-center py-6 text-zinc-500 text-xs" data-testid="trial-codes-empty">
                            No trial codes yet. Add one to get started.
                        </div>
                    ) : (
                        <div className="overflow-x-auto border border-[#222]" data-testid="trial-codes-table-scroll">
                            <table className="w-full min-w-[560px] text-xs">
                                <thead className="bg-[#0a0a0a] border-b border-[#222]">
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                                        <th className="px-3 py-1.5 font-bold">Code</th>
                                        <th className="px-3 py-1.5 font-bold">Description</th>
                                        <th className="px-3 py-1.5 font-bold">Uses</th>
                                        <th className="px-3 py-1.5 font-bold">Status</th>
                                        <th className="px-3 py-1.5 font-bold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codes.map((c) => (
                                        <TrialCodeRow
                                            key={c.id}
                                            code={c}
                                            isEditing={editingId === c.id}
                                            onEdit={() => setEditingId(c.id)}
                                            onCancelEdit={() => setEditingId(null)}
                                            onToggleActive={() => toggleActive(c)}
                                            onDelete={() => removeCode(c)}
                                            onSaved={(updated) => {
                                                setCodes((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
                                                setEditingId(null);
                                            }}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {createOpen && (
                <CreateTrialCodeModal
                    onClose={() => setCreateOpen(false)}
                    onCreated={(c) => {
                        setCodes((prev) => [c, ...prev]);
                        setCreateOpen(false);
                    }}
                />
            )}
        </section>
    );
}

function TrialCodeRow({ code, isEditing, onEdit, onCancelEdit, onToggleActive, onDelete, onSaved }) {
    const [draft, setDraft] = useState({
        description: code.description || "",
        max_uses: code.max_uses ?? "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            if ((draft.description || "") !== (code.description || "")) body.description = draft.description;
            const newMax = draft.max_uses === "" ? null : parseInt(draft.max_uses, 10);
            const oldMax = code.max_uses ?? null;
            if (newMax !== oldMax && newMax !== null && !Number.isNaN(newMax)) body.max_uses = newMax;
            if (Object.keys(body).length === 0) { onCancelEdit(); setSaving(false); return; }
            const r = await api.updateTrialCode(code.id, body);
            toast.success(`Updated ${code.code}`);
            onSaved(r.code);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update code");
        }
        setSaving(false);
    };

    return (
        <tr className={`border-b border-[#222] ${code.active ? "" : "opacity-50"}`} data-testid={`trial-code-row-${code.code}`}>
            <td className="px-3 py-2 font-mono font-bold text-volt">{code.code}</td>
            <td className="px-3 py-2 text-zinc-300">
                {isEditing ? (
                    <input
                        data-testid={`trial-code-desc-input-${code.code}`}
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        placeholder="Optional purpose"
                        className="brutal-input w-full text-xs py-1"
                    />
                ) : (
                    code.description || <span className="text-zinc-600 italic">—</span>
                )}
            </td>
            <td className="px-3 py-2 font-mono text-zinc-400">
                {isEditing ? (
                    <input
                        data-testid={`trial-code-maxuses-input-${code.code}`}
                        type="number"
                        min="1"
                        value={draft.max_uses}
                        onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })}
                        placeholder="∞"
                        className="brutal-input w-20 text-xs py-1"
                    />
                ) : (
                    <>
                        {code.used_count}
                        {code.max_uses ? ` / ${code.max_uses}` : ""}
                    </>
                )}
            </td>
            <td className="px-3 py-2">
                <button
                    type="button"
                    onClick={onToggleActive}
                    data-testid={`trial-code-toggle-${code.code}`}
                    className={`pill text-[10px] font-bold uppercase ${code.active ? "text-cyber border-cyber" : "text-zinc-500 border-[#333]"}`}
                >
                    {code.active ? "Active" : "Inactive"}
                </button>
            </td>
            <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-1">
                    {isEditing ? (
                        <>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                data-testid={`trial-code-save-${code.code}`}
                                className="brutal-btn-primary text-[10px] py-1 px-2"
                            >
                                <Check className="w-3 h-3" />
                            </button>
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                className="brutal-btn-ghost text-[10px] py-1 px-2"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={onEdit}
                                data-testid={`trial-code-edit-${code.code}`}
                                className="brutal-btn-ghost text-[10px] py-1 px-2"
                            >
                                <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                                type="button"
                                onClick={onDelete}
                                data-testid={`trial-code-delete-${code.code}`}
                                className="brutal-btn-ghost text-[10px] py-1 px-2 text-heat hover:text-heat"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

function CreateTrialCodeModal({ onClose, onCreated }) {
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [maxUses, setMaxUses] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim()) { toast.error("Code is required"); return; }
        setBusy(true);
        try {
            const body = {
                code: code.trim().toUpperCase(),
                description: description.trim() || null,
                max_uses: maxUses ? parseInt(maxUses, 10) : null,
                active: true,
            };
            const r = await api.createTrialCode(body);
            toast.success(`Created ${r.code.code}`);
            onCreated(r.code);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create code");
        }
        setBusy(false);
    };

    return (
        <div
            data-testid="create-trial-code-modal"
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="brutal-card p-5 max-w-md w-full space-y-4"
            >
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-extrabold text-lg flex items-center gap-2">
                        <Tag className="w-4 h-4 text-volt" /> New Trial Code
                    </h3>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <label className="block text-xs space-y-1">
                    <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Code</span>
                    <input
                        data-testid="create-trial-code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="TRIAL2026"
                        className="brutal-input w-full font-mono"
                        autoFocus
                        required
                    />
                    <p className="text-[10px] text-zinc-600">Uppercase letters / digits / _ / -, 2–64 chars</p>
                </label>
                <label className="block text-xs space-y-1">
                    <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Description (optional)</span>
                    <input
                        data-testid="create-trial-desc-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Q1 2026 partner trial"
                        className="brutal-input w-full"
                    />
                </label>
                <label className="block text-xs space-y-1">
                    <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Max uses (optional — leave blank for unlimited)</span>
                    <input
                        data-testid="create-trial-maxuses-input"
                        type="number"
                        min="1"
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value)}
                        placeholder="50"
                        className="brutal-input w-full"
                    />
                </label>
                <div className="flex justify-end gap-2 pt-2 border-t border-[#222]">
                    <button type="button" onClick={onClose} className="brutal-btn-ghost text-xs py-2">Cancel</button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="create-trial-submit"
                        className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                    >
                        <Plus className="w-3 h-3" /> {busy ? "Creating…" : "Create"}
                    </button>
                </div>
            </form>
        </div>
    );
}
